/**
 * Hiring floors for the Forward Deployed (FDE) discipline.
 *
 * Single source of truth for the runtime hire-gate: to be recommended at
 * Lk, a candidate must clear EVERY per-skill floor.
 *
 * The `sum` field on each level is inert documentation — it used to be a
 * second gate ("total points across the 5 skills must be ≥ floor.sum") but
 * the total-points gate was dropped 2026-04-29. The field is kept so that
 * (a) the markdown table can still carry a Total row that tests parse, and
 * (b) the gate is trivial to reintroduce without re-shaping the snapshot.
 *
 * Importing modules:
 *   - `server/labs/three-axis-aggregator.ts` — applies the per-skill gate.
 *   - `client/.../results/radar.tsx` — plots the role polygon vertices.
 *
 * The seed markdown at `docs/labs/seed/framework-forward-deployed.md` mirrors
 * these numbers in human-readable form ("Tabela-resumo de mínimos (Hiring)").
 * That table is documentation, NOT runtime — the constants below are the gate.
 * To prevent drift, `__tests__/fde-floors.test.ts` parses the seed table at
 * CI time and asserts it equals the constants. A single PR has to touch both.
 *
 * Why constants instead of parsing the markdown at runtime: hiring floors are
 * policy decisions (changing PM≥3 → PM≥4 changes who gets hired). Edits should
 * go through code review, not the live settings UI where a CHRO could quietly
 * shift the bar.
 */

export const FDE_SKILL_IDS = [
  "project_management",
  "stakeholder_management",
  "business_acumen",
  "hr_fluency",
  "product_sense",
] as const;

export type FdeSkillId = (typeof FDE_SKILL_IDS)[number];
export type FdeLevel = 3 | 4 | 5 | 6;

/**
 * The three skills the compensation level-rule operates on. PM, Agency to
 * influence stakeholders, and Business Acumen are the "core" — a candidate
 * qualifies for L4/L5 based on their max + breadth across these three.
 *
 * HR Fluency and Product Sense (`INFORMATIONAL_FDE_SKILL_IDS`) are
 * intentionally excluded from the gate per the CHRO 2026-05-08 calibration:
 * they're informational, not level-gates. The shared per-skill floor table
 * already had them at 0 across L3-L5; the compensation rule formalises that
 * "they ramp on the job, they don't decide the level."
 */
export const CORE_FDE_SKILL_IDS = [
  "project_management",
  "stakeholder_management",
  "business_acumen",
] as const satisfies readonly FdeSkillId[];

export const INFORMATIONAL_FDE_SKILL_IDS = [
  "hr_fluency",
  "product_sense",
] as const satisfies readonly FdeSkillId[];

/**
 * Compensation-rule level implied by the three core FDE skills. Ratified by
 * the CHRO 2026-05-08 (rationale in commit 40aa2a12). Formal:
 *
 *   L5 = max(core) ≥ 4  AND  count(core ≥ 3) ≥ 2
 *   L4 = max(core) ≥ 4  OR   count(core ≥ 3) ≥ 2
 *   L3 = fallback
 *
 * Plain English: "one exceptional skill plus competence across breadth lifts
 * to L5; either one of those alone lifts to L4." Leonardo (pm=2.5, sm=3.5,
 * ba=4) is L5 because his ba=4 plus sm=3.5 + ba=4 ≥ 3 carry the level even
 * though pm < 4.
 *
 * Validated 10/10 against the calibrated corpus (named gold fixtures); see
 * `server/scripts/v2-level-validate.ts` for the harness.
 */
export function impliedFdeLevelFromCoreSkills(scores: {
  project_management: number;
  stakeholder_management: number;
  business_acumen: number;
}): 3 | 4 | 5 {
  const core = [scores.project_management, scores.stakeholder_management, scores.business_acumen];
  const max = Math.max(...core);
  const countAtCompetency = core.filter((v) => v >= 3).length;
  if (max >= 4 && countAtCompetency >= 2) return 5;
  if (max >= 4 || countAtCompetency >= 2) return 4;
  return 3;
}

export interface FdeSkillFloor {
  perSkill: Record<FdeSkillId, number>;
  sum: number;
}

export type ResolvedFdeFloors = Record<FdeLevel, FdeSkillFloor>;

// HF and PS have a per-skill floor of 0 — those skills ramp on the job and
// are not hiring gates. The `sum` field is inert (see file header); the
// numbers preserve the historical "Total mínimo" so the markdown table
// keeps validating and so we can revisit the gate without reshaping data.
export const FDE_SKILL_LEVEL_FLOORS: Record<FdeLevel, FdeSkillFloor> = {
  3: {
    perSkill: {
      project_management: 3,
      stakeholder_management: 2,
      business_acumen: 2,
      hr_fluency: 0,
      product_sense: 0,
    },
    sum: 10,
  },
  4: {
    perSkill: {
      project_management: 3,
      stakeholder_management: 3,
      business_acumen: 2,
      hr_fluency: 0,
      product_sense: 0,
    },
    sum: 15,
  },
  5: {
    perSkill: {
      project_management: 4,
      stakeholder_management: 4,
      business_acumen: 3,
      hr_fluency: 0,
      product_sense: 0,
    },
    sum: 20,
  },
  6: {
    perSkill: {
      project_management: 4,
      stakeholder_management: 4,
      business_acumen: 3,
      hr_fluency: 0,
      product_sense: 0,
    },
    sum: 23,
  },
};

export function isFdeLevel(level: number): level is FdeLevel {
  return level === 3 || level === 4 || level === 5 || level === 6;
}

export function isFdeSkillId(id: string): id is FdeSkillId {
  return (FDE_SKILL_IDS as readonly string[]).includes(id);
}

/**
 * Per-skill required dots for an FDE skill at any role level — including
 * levels below the floor table. FDE only hires L3+, so L1/L2 floors aren't
 * authored in `floors`. But the radar still has to draw an L−1 polygon when
 * applied=L3, and a uniform "L2 = 2 dots" ring would misrepresent the
 * per-skill shape (HF/PS would jump to 2 even though they ramp on the job).
 *
 * Resolution:
 *   - level ∈ {3,4,5,6}: read directly from the floor table.
 *   - level < 3: subtract one dot per missing level from L3, clamped at 0.
 *     (L2 = max(0, L3 − 1); L1 = max(0, L3 − 2); etc.) Preserves the
 *     per-skill shape and reflects "L2 is one rung easier than L3."
 *   - level > 6: use L6 (defensive; the caller clamps to ≤6 in practice).
 */
export function fdeRequiredDotsAtLevel(
  skillId: FdeSkillId,
  level: number,
  floors: ResolvedFdeFloors,
): number {
  const clamp = (n: number) => Math.max(0, Math.min(5, n));
  // Legacy snapshots predate L6 and only carry floors for {3,4,5}. Walk down
  // until we find a level that exists in the table — any consumer asking for
  // a level that isn't snapshotted gets the closest defined level below it.
  // Falls back to L3 (the lowest authored level) when nothing below is found.
  const pickFloor = (target: FdeLevel): FdeSkillFloor | undefined => {
    const order: FdeLevel[] = [6, 5, 4, 3];
    const start = order.indexOf(target);
    if (start < 0) return undefined;
    for (let i = start; i < order.length; i++) {
      const candidate = floors[order[i]];
      if (candidate) return candidate;
    }
    return undefined;
  };
  if (isFdeLevel(level)) {
    const floor = pickFloor(level);
    if (!floor) return 0;
    return clamp(floor.perSkill[skillId]);
  }
  if (level < 3) {
    const floor = pickFloor(3);
    if (!floor) return 0;
    return clamp(floor.perSkill[skillId] - (3 - level));
  }
  const floor = pickFloor(6);
  if (!floor) return 0;
  return clamp(floor.perSkill[skillId]);
}

/**
 * Parse the "Tabela-resumo de mínimos (Hiring)" table out of the FDE
 * framework markdown. Returns `null` when the table can't be located OR
 * when any cell fails validation (non-integer, out-of-range, missing row).
 *
 * Callers should fall back to `FDE_SKILL_LEVEL_FLOORS` on null — that way a
 * malformed CHRO edit (e.g., a typo, swapped column header) cannot silently
 * downgrade the runtime gate. A `null` return is the parser's way of saying
 * "I'm not confident; use the safety net."
 *
 * Robustness guarantees:
 *   - Header-aware column mapping. Column order in the markdown table can
 *     change ("L5 mínimo | L4 mínimo | L3 mínimo") without breaking the gate.
 *   - Inline markdown (bold, italic, backticks) on cells is stripped.
 *   - Skill labels match a normalized form: case- and whitespace-insensitive,
 *     punctuation-tolerant. Both the current label ("Agency to influence
 *     stakeholders") and the legacy form ("Stakeholder Mgmt.") parse to
 *     the same id; this lets older markdown / cached fixtures keep working.
 *   - Every cell must parse to an integer in [0, 5]. Otherwise → null.
 *   - The total row must be present and parse cleanly. Otherwise → null.
 *   - All 5 FDE skill rows must be present. Otherwise → null.
 */
const SKILL_LABEL_TO_ID: { match: RegExp; id: FdeSkillId }[] = [
  { match: /^project\s*management$/i, id: "project_management" },
  // The id stays `stakeholder_management` to avoid a DB / corpus-wide rename.
  // The display label was renamed 2026-05-08 to "Agency to influence
  // stakeholders" because that's what the rubric was always trying to
  // measure ("agir intencionalmente para influenciá-los"); the old
  // "Stakeholder Mgmt." label is kept here for backwards compat.
  {
    match: /^(agency\s*to\s*influence\s*stakeholders?|stakeholder\s*(management|mgmt\.?))$/i,
    id: "stakeholder_management",
  },
  { match: /^business\s*acumen$/i, id: "business_acumen" },
  { match: /^hr\s*fluency$/i, id: "hr_fluency" },
  { match: /^product\s*sense$/i, id: "product_sense" },
];

function stripInlineMarkdown(s: string): string {
  return s
    .replace(/\*\*/g, "")
    .replace(/__/g, "")
    .replace(/\*/g, "")
    .replace(/_/g, "")
    .replace(/`/g, "")
    .trim();
}

function cellsOf(row: string): string[] {
  return row
    .split("|")
    .slice(1, -1)
    .map((c) => stripInlineMarkdown(c));
}

/**
 * Resolve a display label (with or without the historical "Stakeholder
 * Management" / "Agency to influence stakeholders" rename) to the stable
 * canonical FDE skill ID. Returns `undefined` when the label doesn't match
 * any known FDE skill — caller decides whether to fall through to a
 * generic slugifier or treat the row as out-of-discipline.
 *
 * Exposed so the Call-5 → aggregator path can canonicalize names *before*
 * slugifying; otherwise the LLM emitting "Agency to influence stakeholders"
 * yields `agency_to_influence_stakeholders`, which doesn't match any of the
 * IDs the discipline spec expects, silently disabling the compensation rule
 * and dropping every candidate to L1.
 */
export function canonicalFdeSkillId(label: string): FdeSkillId | undefined {
  const norm = label.replace(/\s+/g, " ").trim();
  for (const { match, id } of SKILL_LABEL_TO_ID) {
    if (match.test(norm)) return id;
  }
  return undefined;
}

function matchSkillLabel(label: string): FdeSkillId | undefined {
  return canonicalFdeSkillId(label);
}

function parseLevelHeader(cell: string): FdeLevel | undefined {
  const m = cell.match(/L\s*([3-6])/i);
  if (!m) return undefined;
  return Number(m[1]) as FdeLevel;
}

function parseDotCell(cell: string): number | undefined {
  const trimmed = cell.trim();
  if (!/^-?\d+$/.test(trimmed)) return undefined;
  const n = Number(trimmed);
  if (!Number.isInteger(n) || n < 0 || n > 5) return undefined;
  return n;
}

function parseSumCell(cell: string): number | undefined {
  const trimmed = cell.trim();
  if (!/^-?\d+$/.test(trimmed)) return undefined;
  const n = Number(trimmed);
  // Sum domain is [0, 25] (5 skills × 5 dots).
  if (!Number.isInteger(n) || n < 0 || n > 25) return undefined;
  return n;
}

/**
 * Validate-and-parse the FDE hiring table out of `markdown`. Returns either
 * the parsed floors or a list of human-readable errors describing why the
 * table is unusable. Used by:
 *   - `parseFdeFloorsFromSeed` (returns the floors, drops the errors).
 *   - `validateFdeFloorsMarkdown` (returns the errors, drops the floors).
 *   - the settings UI save handler (blocks the Save click on errors).
 *
 * Error messages are written for a CHRO who edited the table — they should
 * read as actionable guidance ("Linha 'Project Management' está faltando"),
 * not parser internals.
 */
export type FloorParseResult =
  | { ok: true; floors: Record<FdeLevel, FdeSkillFloor> }
  | { ok: false; errors: string[] };

const SKILL_LABEL_DISPLAY: Record<FdeSkillId, string> = {
  project_management: "Project Management",
  stakeholder_management: "Agency to influence stakeholders",
  business_acumen: "Business Acumen",
  hr_fluency: "HR Fluency",
  product_sense: "Product Sense",
};

export function parseAndValidateFdeFloors(markdown: string): FloorParseResult {
  const errors: string[] = [];

  const headerIdx = markdown.indexOf("Tabela-resumo de mínimos (Hiring)");
  if (headerIdx < 0) {
    return {
      ok: false,
      errors: ["Cabeçalho `### Tabela-resumo de mínimos (Hiring)` não encontrado."],
    };
  }
  const rest = markdown.slice(headerIdx);
  const lines = rest.split(/\r?\n/);
  const tableRows: string[] = [];
  let inTable = false;
  for (const line of lines) {
    if (line.trim().startsWith("|")) {
      inTable = true;
      tableRows.push(line);
    } else if (inTable) {
      break;
    }
  }
  if (tableRows.length < 7) {
    return {
      ok: false,
      errors: [
        `Tabela incompleta: esperadas 7 linhas (cabeçalho + separador + 5 skills + total), encontradas ${tableRows.length}.`,
      ],
    };
  }

  const headerCells = cellsOf(tableRows[0]);
  if (headerCells.length < 4) {
    errors.push("Cabeçalho da tabela precisa ter 4 colunas: Skill, L3, L4, L5.");
    return { ok: false, errors };
  }
  const colToLevel = headerCells.map((c, i) => (i === 0 ? undefined : parseLevelHeader(c)));
  const requiredLevels: FdeLevel[] = [3, 4, 5, 6];
  for (const lvl of requiredLevels) {
    if (!colToLevel.includes(lvl)) {
      errors.push(`Coluna L${lvl} não encontrada no cabeçalho da tabela.`);
    }
  }
  if (errors.length > 0) return { ok: false, errors };

  const accumulator: Record<FdeLevel, FdeSkillFloor> = {
    3: { perSkill: {} as Record<FdeSkillId, number>, sum: NaN },
    4: { perSkill: {} as Record<FdeSkillId, number>, sum: NaN },
    5: { perSkill: {} as Record<FdeSkillId, number>, sum: NaN },
    6: { perSkill: {} as Record<FdeSkillId, number>, sum: NaN },
  };
  const seenSkills = new Set<FdeSkillId>();
  let seenTotal = false;

  for (let i = 2; i < tableRows.length; i++) {
    const cells = cellsOf(tableRows[i]);
    if (cells.length < 4) continue;
    const label = cells[0];
    const skillId = matchSkillLabel(label);
    if (skillId) {
      for (let col = 1; col < cells.length; col++) {
        const lvl = colToLevel[col];
        if (lvl === undefined) continue;
        const dots = parseDotCell(cells[col]);
        if (dots === undefined) {
          errors.push(
            `Linha "${SKILL_LABEL_DISPLAY[skillId]}" coluna L${lvl}: "${cells[col]}" não é um inteiro válido em [0, 5].`,
          );
          continue;
        }
        accumulator[lvl].perSkill[skillId] = dots;
      }
      seenSkills.add(skillId);
    } else if (/total/i.test(label)) {
      for (let col = 1; col < cells.length; col++) {
        const lvl = colToLevel[col];
        if (lvl === undefined) continue;
        const sum = parseSumCell(cells[col]);
        if (sum === undefined) {
          errors.push(
            `Linha "Total" coluna L${lvl}: "${cells[col]}" não é um inteiro válido em [0, 25].`,
          );
          continue;
        }
        accumulator[lvl].sum = sum;
      }
      seenTotal = true;
    }
  }

  if (!seenTotal) {
    errors.push("Linha 'Total' (soma mínima) não encontrada.");
  }
  for (const id of FDE_SKILL_IDS) {
    if (!seenSkills.has(id)) {
      errors.push(`Linha "${SKILL_LABEL_DISPLAY[id]}" está faltando.`);
    }
  }
  for (const lvl of requiredLevels) {
    if (Number.isNaN(accumulator[lvl].sum) && seenTotal) {
      errors.push(`Soma mínima para L${lvl} ausente ou ilegível.`);
    }
    for (const id of FDE_SKILL_IDS) {
      if (accumulator[lvl].perSkill[id] === undefined && seenSkills.has(id)) {
        errors.push(`Célula L${lvl} para "${SKILL_LABEL_DISPLAY[id]}" ausente.`);
      }
    }
  }

  if (errors.length > 0) return { ok: false, errors };
  return { ok: true, floors: accumulator };
}

export function parseFdeFloorsFromSeed(markdown: string): Record<FdeLevel, FdeSkillFloor> | null {
  const result = parseAndValidateFdeFloors(markdown);
  return result.ok ? result.floors : null;
}

/**
 * Validate the FDE floor table inside framework markdown. Used by the
 * settings UI to block the Save button when the CHRO edited the table into
 * a state the runtime gate can't parse. Returns a flat list of errors so
 * the UI can show them inline.
 */
export function validateFdeFloorsMarkdown(markdown: string): string[] {
  const result = parseAndValidateFdeFloors(markdown);
  return result.ok ? [] : result.errors;
}

/**
 * Resolve the floor table to use at runtime. Prefers the parsed markdown
 * (so live edits to `scoring_config[framework:forward-deployed]` propagate
 * without a deploy); falls back to the canonical constants when the markdown
 * is missing, the table can't be located, or any cell fails validation.
 *
 * The fallback is the safety net — it guarantees the gate never passes a
 * candidate based on a malformed table. Callers that want to know whether
 * the fallback was used should check `parseFdeFloorsFromSeed` directly.
 */
export function resolveFdeFloors(
  markdown: string | undefined | null,
): Record<FdeLevel, FdeSkillFloor> {
  if (!markdown) return FDE_SKILL_LEVEL_FLOORS;
  const parsed = parseFdeFloorsFromSeed(markdown);
  return parsed ?? FDE_SKILL_LEVEL_FLOORS;
}
